import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ChefHat, Sparkles, Loader2, RefreshCw, ListPlus, BookOpen, Clock, Check, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";

/**
 * ChefsSuggestion — a warm, AI-driven "what can I cook right now" section.
 * Shown at the top of the Pantry tab. Uses the whole pantry (not just expiring items).
 * Respects household cuisine preferences.
 */
export default function ChefsSuggestion({ onGroceryChanged }) {
  const [state, setState] = useState({ recipes: [], pantry_count: 0, cuisines: [], loading: false, loaded: false, error: null, message: null });
  const [showRecipe, setShowRecipe] = useState(null); // recipe object for modal

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.post("/recipes/from-pantry");
      setState({
        recipes: data.recipes || [],
        pantry_count: data.pantry_count || 0,
        cuisines: data.cuisines || [],
        loading: false,
        loaded: true,
        error: data.error || null,
        message: data.message || null,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, loaded: true, error: formatApiError(e.response?.data?.detail) || "Couldn't reach the chef" }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMissing = async (recipe) => {
    const missing = recipe.missing || [];
    if (!missing.length) {
      toast.info("Nothing missing — everything's already in the pantry.");
      return;
    }
    try {
      const { data } = await api.post("/recipes/add-missing", { ingredients: missing });
      toast.success(`Added ${data.count} to grocery`);
      onGroceryChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="warm-card p-6 mb-6 bg-hearth-amber/5 border-hearth-amber/30" data-testid="chefs-suggestion">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-2xl text-hearth-ink inline-flex items-center gap-2">
            <ChefHat size={18} className="text-hearth-clay" /> Chef&apos;s suggestion
          </h3>
          <p className="caption mt-0.5">
            based on what&apos;s in your pantry
            {state.cuisines?.length > 0 && <> · leaning {state.cuisines.slice(0, 3).join(" / ")}</>}
          </p>
        </div>
        <Button
          data-testid="chefs-suggestion-refresh"
          onClick={load}
          disabled={state.loading}
          variant="ghost"
          className="rounded-full border border-hearth-line hover:bg-hearth-stone h-9 text-xs"
        >
          {state.loading
            ? <Loader2 size={13} className="mr-1.5 animate-spin"/>
            : <RefreshCw size={13} className="mr-1.5"/>}
          New ideas
        </Button>
      </div>

      {state.loading && !state.loaded && (
        <div className="text-center py-6">
          <Loader2 size={24} className="mx-auto animate-spin text-hearth-clay" />
          <p className="text-hearth-mute mt-2 font-display italic">Reading your pantry…</p>
        </div>
      )}

      {state.message && !state.recipes.length && (
        <p className="text-sm text-hearth-mute italic">{state.message}</p>
      )}

      {state.error && (
        <p className="text-sm text-hearth-coral" data-testid="chefs-suggestion-error">
          {state.error.toLowerCase().includes("budget")
            ? "Chef AI is on a coffee break — top up Universal Key balance to resume."
            : "Couldn't fetch suggestions right now."}
        </p>
      )}

      {state.recipes.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {state.recipes.map((r, idx) => {
            const missing = r.missing?.length || 0;
            const have = r.have?.length || 0;
            const total = missing + have;
            return (
              <article
                key={`${r.title}-${idx}`}
                data-testid={`chefs-recipe-${idx}`}
                className="warm-card p-4 flex flex-col bg-hearth-paper"
              >
                <h4 className="font-display text-lg text-hearth-ink leading-tight">{r.title}</h4>
                <div className="caption mt-1 flex items-center gap-2 flex-wrap">
                  {r.time_min != null && <span className="inline-flex items-center gap-1"><Clock size={10}/> ~{r.time_min}m</span>}
                  {r.difficulty && <span>{r.difficulty}</span>}
                </div>
                {r.blurb && <p className="text-xs text-hearth-mute mt-2 italic leading-relaxed line-clamp-3">{r.blurb}</p>}

                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`chefs-recipe-missing-count-${idx}`}>
                  <span className="chip bg-hearth-sage/15 text-hearth-sageDark inline-flex items-center gap-1">
                    <Check size={10}/> {have} have
                  </span>
                  {missing > 0 ? (
                    <span className="chip bg-hearth-coral/12 text-hearth-coral inline-flex items-center gap-1">
                      <ShoppingBag size={10}/> {missing} missing
                    </span>
                  ) : (
                    <span className="chip bg-hearth-sage/20 text-hearth-sageDark inline-flex items-center gap-1">
                      <Sparkles size={10}/> ready to cook
                    </span>
                  )}
                  {total > 0 && (
                    <span className="caption font-mono">{have}/{total}</span>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-hearth-line/60 flex items-center gap-1.5 flex-wrap mt-auto">
                  <Button
                    data-testid={`chefs-recipe-show-${idx}`}
                    onClick={() => setShowRecipe(r)}
                    variant="ghost"
                    className="rounded-full border border-hearth-line hover:bg-hearth-stone h-8 text-xs flex-1"
                  >
                    <BookOpen size={12} className="mr-1"/> Show recipe
                  </Button>
                  <Button
                    data-testid={`chefs-recipe-add-missing-${idx}`}
                    onClick={() => addMissing(r)}
                    disabled={missing === 0}
                    className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark disabled:bg-hearth-stone disabled:text-hearth-mute text-white h-8 text-xs flex-1"
                  >
                    <ListPlus size={12} className="mr-1"/> Add missing
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Recipe detail modal */}
      <Dialog open={!!showRecipe} onOpenChange={(o) => !o && setShowRecipe(null)}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold">
              {showRecipe?.title}
            </DialogTitle>
          </DialogHeader>
          {showRecipe && (
            <div className="space-y-4">
              <div className="caption flex items-center gap-3 flex-wrap">
                {showRecipe.time_min != null && <span className="inline-flex items-center gap-1"><Clock size={11}/> ~{showRecipe.time_min} min</span>}
                {showRecipe.difficulty && <span>{showRecipe.difficulty}</span>}
              </div>
              {showRecipe.blurb && (
                <p className="text-sm text-hearth-mute italic leading-relaxed">{showRecipe.blurb}</p>
              )}

              <div>
                <p className="caption mb-2">Ingredients</p>
                <div className="flex flex-wrap gap-1.5" data-testid="chefs-recipe-modal-ingredients">
                  {(showRecipe.have || []).map((h) => (
                    <span key={`h-${h}`} className="chip bg-hearth-sage/15 text-hearth-sageDark inline-flex items-center gap-1">
                      <Check size={10}/> {h}
                    </span>
                  ))}
                  {(showRecipe.missing || []).map((m) => (
                    <span key={`m-${m}`} className="chip bg-hearth-coral/12 text-hearth-coral inline-flex items-center gap-1">
                      <ShoppingBag size={10}/> {m}
                    </span>
                  ))}
                </div>
              </div>

              {showRecipe.steps?.length > 0 && (
                <div>
                  <p className="caption mb-2">Steps</p>
                  <ol className="space-y-2 text-sm">
                    {showRecipe.steps.map((step, sidx) => (
                      <li key={sidx} className="flex gap-2">
                        <span className="w-5 h-5 rounded-full bg-hearth-stone text-hearth-ink text-xs font-mono grid place-items-center shrink-0 mt-0.5">
                          {sidx + 1}
                        </span>
                        <span className="text-hearth-ink leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {(showRecipe.missing?.length || 0) > 0 && (
                <div className="pt-3 border-t border-hearth-line/60">
                  <Button
                    data-testid="chefs-recipe-modal-add-missing"
                    onClick={() => { addMissing(showRecipe); setShowRecipe(null); }}
                    className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white w-full"
                  >
                    <ListPlus size={13} className="mr-1.5"/> Add {showRecipe.missing.length} missing to grocery
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
